import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  MicrophoneIcon, 
  ChatBubbleBottomCenterTextIcon,
  EnvelopeIcon,
  UserGroupIcon,
  ChartBarIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DeploymentCountdown from '../components/DeploymentCountdown';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalMessages: 0,
    totalRecipients: 0,
    pendingDeliveries: 0,
    voiceCloned: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Demo mode - show sample data if not logged in
      const userId = user ? user.id : 'demo_user';
      
      // Fetch user stats
      const [messagesRes, recipientsRes, progressRes] = await Promise.all([
        axios.get(`${API_URL}/recordings/user/${userId}`),
        axios.get(`${API_URL}/recipients/user/${userId}`),
        axios.get(`${API_URL}/podcast/progress/${userId}`).catch(() => ({ data: {} }))
      ]);

      const session = progressRes.data.session || {};

      setStats({
        totalMessages: messagesRes.data.total || 0,
        totalRecipients: recipientsRes.data.total || 0,
        pendingDeliveries: 0,
        voiceCloned: !!session.voice_id,
        voiceId: session.voice_id,
        previewAudioUrl: session.preview_audio_url,
        finalPodcastUrl: session.final_podcast_url
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      name: 'Record Message',
      description: 'Record a quick message for a loved one',
      href: '/record',
      icon: MicrophoneIcon,
      color: 'bg-blue-500'
    },
    {
      name: 'Start Podcast',
      description: 'Begin an AI-guided interview session',
      href: '/podcast',
      icon: ChatBubbleBottomCenterTextIcon,
      color: 'bg-purple-500'
    },
    {
      name: 'View Messages',
      description: 'Manage your recorded messages',
      href: '/messages',
      icon: EnvelopeIcon,
      color: 'bg-green-500'
    },
    {
      name: 'Manage Recipients',
      description: 'Add or edit recipients',
      href: '/recipients',
      icon: UserGroupIcon,
      color: 'bg-orange-500'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 rounded-3xl mb-12 shadow-2xl">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative px-8 py-16 sm:px-12 sm:py-20">
          <div className="max-w-3xl">
            <h1 className="text-5xl sm:text-6xl font-extrabold text-white mb-6 leading-tight">
              {user ? `Welcome back, ${user.name}` : "Stay Close While You're Away"}
            </h1>
            <p className="text-xl sm:text-2xl text-white/90 mb-8 leading-relaxed">
              Deployed? Traveling? Send personalized voice messages to your family every day. 
              Bedtime stories, birthday wishes, "I love you" — in YOUR voice, on their phone.
            </p>
            {!user && (
              <div className="flex gap-4">
                <Link
                  to="/login"
                  className="px-8 py-4 bg-white text-purple-600 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Get Started Free
                </Link>
                <a
                  href="#how-it-works"
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-bold text-lg hover:bg-white/20 transition-all border-2 border-white/30"
                >
                  Learn More
                </a>
              </div>
            )}
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 -mt-12 -mr-12 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -mb-12 -ml-12 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
      </div>

      {/* Remove conditional voice clone section - keep layout consistent */}

      {/* Getting Started Guide */}
      {!loading && user && (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl">🚀</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Get Started in 3 Steps</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-start p-6 rounded-xl hover:bg-gray-50 transition-all">
              <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${stats.voiceCloned ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg' : 'bg-gradient-to-br from-gray-300 to-gray-400'} text-white font-bold mr-4 text-lg`}>
                {stats.voiceCloned ? '✓' : '1'}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">💝 Share Your Heart</h3>
                <p className="text-sm text-gray-600 mb-2">Record your voice once. We'll preserve it forever - your laugh, your wisdom, the way you say "I love you."</p>
                {stats.voiceCloned ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-2">
                    <p className="text-sm text-green-800 font-medium mb-2">✓ Your AI voice is ready!</p>
                    <div className="flex gap-2">
                      <a href="/voice-clone" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                        View Voice Clone →
                      </a>
                      <span className="text-gray-400">|</span>
                      <a href="/podcast" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                        Record Podcast →
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <a href="/voice-clone" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                      Clone Your Voice →
                    </a>
                    <span className="text-gray-400">|</span>
                    <a href="/podcast" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                      Record Podcast →
                    </a>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-start p-6 rounded-xl hover:bg-gray-50 transition-all">
              <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${stats.totalRecipients > 0 ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg' : 'bg-gradient-to-br from-gray-300 to-gray-400'} text-white font-bold mr-4 text-lg`}>
                {stats.totalRecipients > 0 ? '✓' : '2'}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">👨‍👩‍👧‍👦 Choose Who You&rsquo;ll Comfort</h3>
                <p className="text-sm text-gray-600 mb-2">Your daughter on her wedding day. Your son when he needs advice. Your grandkids when they miss you.</p>
                {stats.totalRecipients === 0 && (
                  <a href="/recipients" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                    Add Family Members →
                  </a>
                )}
              </div>
            </div>
            
            <div className="flex items-start p-6 rounded-xl hover:bg-gray-50 transition-all">
              <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${stats.totalMessages > 0 ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg' : 'bg-gradient-to-br from-gray-300 to-gray-400'} text-white font-bold mr-4 text-lg`}>
                {stats.totalMessages > 0 ? '✓' : '3'}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">✍️ Write From the Heart</h3>
                <p className="text-sm text-gray-600 mb-2">"Happy birthday." "I believe in you." "Everything will be okay." Simple words that mean everything when they hear YOUR voice.</p>
                {stats.totalMessages === 0 && stats.totalRecipients > 0 && stats.voiceCloned && (
                  <a href="/messages/create" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                    Create Messages →
                  </a>
                )}
                {stats.totalMessages > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                    <p className="text-sm text-blue-800 font-medium">{stats.totalMessages} message{stats.totalMessages !== 1 ? 's' : ''} created</p>
                    <p className="text-xs text-blue-600 mt-1">Each family member has their own portal to access their messages</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Delivery Section */}
      {!loading && user && stats.totalRecipients > 0 && (
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <div className="flex items-center mb-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4">
              <span className="text-3xl">📱</span>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-1">💚 Daily Love Delivered</h2>
              <p className="text-green-100">Every morning at 7am, they hear you say "Good morning, I love you"</p>
            </div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white/80">Total Recipients</span>
                <span className="text-2xl font-bold text-white">{stats.totalRecipients}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white/80">Active Subscriptions</span>
                <span className="text-2xl font-bold text-white">0</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white/80">Messages Sent Today</span>
                <span className="text-2xl font-bold text-white">0</span>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-white/20">
              <p className="text-sm font-semibold text-white mb-3">How it works:</p>
              <ul className="text-sm text-white/90 space-y-2">
                <li>✓ Add family members with their WhatsApp numbers</li>
                <li>✓ They receive an invitation to activate</li>
                <li>✓ They choose their preferred delivery time</li>
                <li>✓ Daily messages arrive automatically with audio</li>
              </ul>
            </div>
            
            <Link
              to="/recipients"
              className="mt-6 block w-full px-6 py-3 bg-white text-green-600 text-center rounded-xl hover:bg-green-50 font-bold text-lg shadow-lg hover:shadow-xl transition-all"
            >
              Manage Recipients →
            </Link>
          </div>
        </div>
      )}

      {/* Main Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        <Link
          to="/recipients"
          className="group bg-white rounded-2xl p-8 hover:shadow-2xl transition-all border border-gray-100 hover:border-purple-200 transform hover:-translate-y-1"
        >
          <div className="flex items-start">
            <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-pink-500 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
              <UserGroupIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                The People You Love
              </h3>
              <p className="text-gray-600 mb-3">
                Who needs to hear your voice today? Your kids, your partner, the people who matter most.
              </p>
              {stats.totalRecipients > 0 && (
                <p className="text-xs text-green-600 mt-1 font-medium">
                  {stats.totalRecipients} recipient{stats.totalRecipients !== 1 ? 's' : ''} added
                </p>
              )}
            </div>
          </div>
        </Link>

        <Link
          to="/podcast"
          className="group bg-white rounded-2xl p-8 hover:shadow-2xl transition-all border border-gray-100 hover:border-purple-200 transform hover:-translate-y-1"
        >
          <div className="flex items-start">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
              <ChatBubbleBottomCenterTextIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                Preserve Your Voice
              </h3>
              <p className="text-gray-600">
                Not just the sound - your warmth, your humor, the way you make them feel safe.
              </p>
            </div>
          </div>
        </Link>
      </div>

      {/* How It Works - Story-like explanation */}
      <div id="how-it-works" className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-12 shadow-2xl">
        <h2 className="text-4xl font-bold text-white mb-8">
          💌 A Gift That Lasts Forever
        </h2>
        <p className="text-xl text-gray-300 mb-10 leading-relaxed">
          Imagine: Your daughter's having a tough day. She picks up her phone. There's a message from you - in YOUR voice - telling her she's strong, she's loved, she can do this. That's what we help you create.
        </p>
        <div className="space-y-8">
          <div className="flex items-start bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mr-6 flex-shrink-0">
              <span className="text-3xl font-bold text-white">1</span>
            </div>
            <div>
              <p className="text-2xl font-bold text-white mb-3">Tell us who needs you</p>
              <p className="text-lg text-gray-300">Your daughter who's becoming a mom. Your son starting his career. The grandkids you won't get to see grow up. We'll make sure they hear you.</p>
            </div>
          </div>
          <div className="flex items-start bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-6 flex-shrink-0">
              <span className="text-3xl font-bold text-white">2</span>
            </div>
            <div>
              <p className="text-2xl font-bold text-white mb-3">Speak from your heart</p>
              <p className="text-lg text-gray-300">"I'm proud of you." "Remember when we..." "You're going to be amazing." Just talk like you always do. We'll capture everything - even the warmth in your voice.</p>
            </div>
          </div>
          <div className="flex items-start bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-6 flex-shrink-0">
              <span className="text-3xl font-bold text-white">3</span>
            </div>
            <div>
              <p className="text-2xl font-bold text-white mb-3">They wake up to you</p>
              <p className="text-lg text-gray-300">7am. Their phone buzzes. It's you. "Good morning, sweetheart. I hope today brings you joy." In your voice. Every single day. They're never alone.</p>
            </div>
          </div>
        </div>
        {!user && (
          <div className="mt-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-center">
            <p className="text-2xl font-bold text-white mb-6">
              Ready to be there for them, always?
            </p>
            <p className="text-lg text-white/80 mb-6">
              Because the greatest gift you can give is knowing they&rsquo;ll always hear you say &ldquo;I love you.&rdquo;
            </p>
            <Link
              to="/login"
              className="inline-block px-10 py-4 bg-white text-purple-600 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5"
            >
              Get Started Free →
            </Link>
          </div>
        )}
      </div>

      {/* Contact Us Section */}
      <div className="mt-12 bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
        <div className="flex justify-center mb-4">
          <QuestionMarkCircleIcon className="h-12 w-12 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3">
          Need Help or Have Questions?
        </h2>
        <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
          We're here to help! Whether you have questions about our service, need technical support, or just want to share feedback, we'd love to hear from you.
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg"
        >
          <EnvelopeIcon className="h-5 w-5 mr-2" />
          Contact Us
        </Link>
      </div>
    </div>
  );
}
